import React, { useCallback, useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, UserPlus, UserCheck, MessageCircle, Globe, Instagram, Pencil, Check, X, Users, Heart, Lock } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { media, KIND_LABEL, fileToDataUrl } from "../lib/community";
import { Avatar, Byline, KindBadge, PostCard, Composer } from "../components/community/CommunityKit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

function Shell({ children, title, path }) {
  const { user } = useAuth();
  const tab = ({ isActive }) => `relative px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.18em] whitespace-nowrap transition-colors ${isActive ? "bg-[var(--ink)] text-white" : "text-[var(--muted)] hover:text-[var(--ink)]"}`;
  return (
    <div className="bg-[var(--bg)] min-h-screen overflow-x-clip">
      <Seo title={title} description="The LockedInForever wedding community: couples, planners, venues and vendors sharing real weddings." path={path} />
      <Navbar />
      <div className="container-wide px-4 md:px-12 pt-6 md:pt-10">
        <p className="font-script text-[var(--gold)] text-3xl md:text-4xl">The Community</p>
        <nav className="flex gap-1 mt-3 overflow-x-auto no-scrollbar" data-testid="community-subnav">
          <NavLink to="/community" end className={tab} data-testid="community-tab-feed">Feed</NavLink>
          <NavLink to="/community/directory" className={tab} data-testid="community-tab-directory">Find pros</NavLink>
          {user && <NavLink to="/messages" className={tab} data-testid="community-tab-messages">Messages</NavLink>}
          {user && <NavLink to="/community/me" className={tab} data-testid="community-tab-me">My profile</NavLink>}
        </nav>
      </div>
      <div className="container-wide px-4 md:px-12 py-6 md:py-8">{children}</div>
      <Footer />
    </div>
  );
}

function Requests({ onChange }) {
  const [c, setC] = useState({ incoming: [] });
  const load = useCallback(() => api.get("/community/connections").then((r) => setC(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  async function respond(id, accept) {
    await api.post(`/community/connections/${id}/respond`, { accept });
    toast.success(accept ? "Connected" : "Request declined");
    load(); onChange?.();
  }
  if (!c.incoming.length) return null;
  return (
    <div className="bg-white border border-[var(--line)] rounded-3xl p-4" data-testid="connection-requests">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] mb-3">Connection requests</p>
      {c.incoming.map((r) => r.member && (
        <div key={r.conn_id} className="flex items-center justify-between gap-2 py-2">
          <Byline p={r.member} />
          <div className="flex gap-1 shrink-0">
            <button onClick={() => respond(r.conn_id, true)} data-testid={`conn-accept-${r.conn_id}`} className="w-8 h-8 rounded-full bg-[var(--gold)] text-white flex items-center justify-center"><Check className="w-4 h-4" /></button>
            <button onClick={() => respond(r.conn_id, false)} data-testid={`conn-decline-${r.conn_id}`} className="w-8 h-8 rounded-full border border-[var(--line)] flex items-center justify-center"><X className="w-4 h-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Suggestions() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/community/suggestions").then((r) => setRows(r.data)).catch(() => {}); }, []);
  if (!rows.length) return null;
  return (
    <div className="bg-white border border-[var(--line)] rounded-3xl p-4" data-testid="suggestions">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] mb-2">People to follow</p>
      {rows.map((p) => <div key={p.user_id} className="py-2"><Byline p={p} /></div>)}
    </div>
  );
}

export function CommunityFeedPage() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [scope, setScope] = useState("all");
  const [posts, setPosts] = useState(null);
  const [more, setMore] = useState(true);

  useEffect(() => { if (user) api.get("/community/me").then((r) => setMe(r.data)).catch(() => {}); }, [user]);
  useEffect(() => {
    setPosts(null);
    api.get(`/community/feed?scope=${scope}`).then((r) => { setPosts(r.data); setMore(r.data.length === 20); }).catch(() => setPosts([]));
  }, [scope]);
  async function loadMore() {
    const last = posts[posts.length - 1];
    const { data } = await api.get(`/community/feed?scope=${scope}&before=${encodeURIComponent(last.created_at)}`);
    setPosts([...posts, ...data]); setMore(data.length === 20);
  }

  return (
    <Shell title="Wedding Community Feed" path="/community">
      <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
        <div className="space-y-4 min-w-0 max-w-2xl" data-testid="community-feed">
          {user && me && <Composer me={me} onPosted={(p) => setPosts((x) => [p, ...(x || [])])} />}
          {!user && (
            <div className="bg-[var(--ink)] text-white rounded-3xl p-5 flex items-center justify-between gap-4">
              <p className="font-serif text-xl">Join the conversation.</p>
              <Link to="/signup" className="btn-gold !py-2.5" data-testid="community-join-btn">Join free</Link>
            </div>
          )}
          {user && (
            <div className="flex gap-2">
              {["all", "following"].map((s) => (
                <button key={s} onClick={() => setScope(s)} data-testid={`feed-scope-${s}`} className={`text-xs px-4 py-1.5 rounded-full border ${scope === s ? "border-[var(--gold)] text-[var(--ink)] bg-white" : "border-transparent text-[var(--muted)]"}`}>{s === "all" ? "Everyone" : "Following"}</button>
              ))}
            </div>
          )}
          {posts === null && [0, 1].map((i) => <div key={i} className="h-40 rounded-3xl bg-white/70 animate-pulse" />)}
          {posts?.length === 0 && <p className="text-center text-[var(--muted)] py-16" data-testid="feed-empty">No posts yet. Be the first to share something beautiful.</p>}
          {posts?.map((p) => <PostCard key={p.post_id} post={p} user={user} onDeleted={(id) => setPosts((x) => x.filter((y) => y.post_id !== id))} />)}
          {posts?.length > 0 && more && <button onClick={loadMore} className="btn-outline-gold w-full" data-testid="feed-load-more">Load more</button>}
        </div>
        {user && <aside className="space-y-4 lg:sticky lg:top-24"><Requests /><Suggestions /></aside>}
      </div>
    </Shell>
  );
}

function RelationButtons({ p, reload }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const rel = p.relationship;
  const need = () => { if (!user) { nav(`/login?next=/community/u/${p.handle}`); return true; } return false; };
  async function follow() { if (need()) return; try { await api.post(`/community/follow/${p.user_id}`); reload(); } catch (e) { toast.error(formatApiError(e)); } }
  async function connect() { if (need()) return; try { const { data } = await api.post(`/community/connect/${p.user_id}`); toast.success(data.status === "accepted" ? "Connected" : "Request sent"); reload(); } catch (e) { toast.error(formatApiError(e)); } }
  async function message() { if (need()) return; try { const { data } = await api.post("/dm/threads", { user_id: p.user_id }); nav(`/messages/${data.thread_id}`); } catch (e) { toast.error(formatApiError(e)); } }
  const c = rel.connection;
  const connLabel = !c ? "Connect" : c.status === "accepted" ? "Connected" : c.incoming ? "Accept request" : "Requested";
  return (
    <div className="flex flex-wrap gap-2">
      <button onClick={follow} data-testid="profile-follow-btn" className={rel.following ? "btn-outline-gold !py-2.5" : "btn-gold !py-2.5"}>{rel.following ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}{rel.following ? "Following" : "Follow"}</button>
      <button onClick={connect} disabled={c && (c.status === "accepted" || (c.status === "pending" && !c.incoming))} data-testid="profile-connect-btn" className="btn-outline-gold !py-2.5 disabled:opacity-60"><Users className="w-4 h-4" /> {connLabel}</button>
      <button onClick={message} data-testid="profile-message-btn" className="btn-outline-gold !py-2.5"><MessageCircle className="w-4 h-4" /> Message</button>
    </div>
  );
}

function EditProfile({ p, open, onClose, onSaved }) {
  const [f, setF] = useState(p);
  const [busy, setBusy] = useState(false);
  useEffect(() => setF(p), [p]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  async function img(k, e) { try { setF({ ...f, [k]: await fileToDataUrl(e.target.files[0], k === "avatar_data_url" ? 5 : 10) }); } catch (err) { toast.error(err.message); } }
  async function save() {
    setBusy(true);
    try {
      const keys = ["display_name", "handle", "headline", "bio", "kind", "city", "state", "price_range", "website", "instagram", "avatar_data_url", "cover_data_url", "wall_visibility", "location_mode"];
      const body = Object.fromEntries(keys.filter((k) => f[k] !== undefined && f[k] !== null).map((k) => [k, f[k]]));
      const { data } = await api.put("/community/me", body);
      toast.success("Profile saved"); onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  const inp = (k, label, props = {}) => <label className="block"><span className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">{label}</span><input value={f[k] || ""} onChange={set(k)} data-testid={`edit-${k}`} className="input-luxe" {...props} /></label>;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="edit-profile-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl font-light">Edit profile</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {inp("display_name", "Name")}
          {inp("handle", "Handle (a-z, 0-9, _)")}
          <div className="sm:col-span-2">{inp("headline", "Headline")}</div>
          <label className="block"><span className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">I am a</span>
            <select value={f.kind} onChange={set("kind")} data-testid="edit-kind" className="input-luxe">{Object.entries(KIND_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
          <label className="block"><span className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Price range</span>
            <select value={f.price_range || ""} onChange={set("price_range")} data-testid="edit-price_range" className="input-luxe">{["", "$", "$$", "$$$", "$$$$"].map((v) => <option key={v} value={v}>{v || "—"}</option>)}</select></label>
          {inp("city", "City")}
          {inp("state", "State")}
          {inp("website", "Website")}
          {inp("instagram", "Instagram")}
          <label className="block sm:col-span-2"><span className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Bio</span><textarea rows={3} value={f.bio || ""} onChange={set("bio")} data-testid="edit-bio" className="input-luxe resize-none" /></label>
          <label className="block"><span className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Who sees my wall</span>
            <select value={f.wall_visibility || "public"} onChange={set("wall_visibility")} data-testid="edit-wall_visibility" className="input-luxe"><option value="guests">My guests & people I approve</option><option value="public">Everyone</option></select></label>
          <label className="block"><span className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Wedding city & state</span>
            <select value={f.location_mode || "after_wedding"} onChange={set("location_mode")} data-testid="edit-location_mode" className="input-luxe"><option value="after_wedding">Show after the wedding</option><option value="hidden">Keep hidden</option><option value="public">Show now</option></select></label>
          <p className="sm:col-span-2 text-[11px] text-[var(--muted)] -mt-1">Only the city and state are ever shown, never your venue or address.</p>
          <label className="text-xs text-[var(--gold)] cursor-pointer">Change photo<input type="file" accept="image/*" className="hidden" onChange={(e) => img("avatar_data_url", e)} data-testid="edit-avatar" /></label>
          <label className="text-xs text-[var(--gold)] cursor-pointer">Change cover<input type="file" accept="image/*" className="hidden" onChange={(e) => img("cover_data_url", e)} data-testid="edit-cover" /></label>
        </div>
        <button onClick={save} disabled={busy} className="btn-gold w-full mt-2" data-testid="edit-save">{busy ? "Saving…" : "Save profile"}</button>
      </DialogContent>
    </Dialog>
  );
}

export function ProfilePage() {
  const { handle = "me" } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(false);
  const [mine, setMine] = useState(null);
  useEffect(() => { if (user) api.get("/community/me").then((r) => setMine(r.data)).catch(() => {}); }, [user]);

  const load = useCallback(async () => {
    try {
      let h = handle;
      if (handle === "me") { if (loading) return; if (!user) { nav("/login?next=/community/me"); return; } h = (await api.get("/community/me")).data.handle; }
      const [a, b] = await Promise.all([api.get(`/community/profiles/${h}`), api.get(`/community/profiles/${h}/wall`).catch((e) => (e?.response?.status === 403 ? { data: null } : Promise.reject(e)))]);
      setP(a.data); setPosts(b.data);
    } catch { setP(false); }
  }, [handle, user, loading, nav]);
  useEffect(() => { load(); }, [load]);

  if (p === false) return <Shell title="Profile" path="/community"><p className="text-center py-20 text-[var(--muted)]">Profile not found.</p></Shell>;
  if (!p) return <Shell title="Profile" path="/community"><div className="h-64 rounded-3xl bg-white/70 animate-pulse" /></Shell>;
  return (
    <Shell title={`${p.display_name} — LockedInForever Community`} path={`/community/u/${p.handle}`}>
      <div className="bg-white border border-[var(--line)] rounded-[2rem] overflow-hidden" data-testid="profile-card">
        <div className="h-36 md:h-56 bg-[var(--ink)] relative">{p.cover_url && <img src={media(p.cover_url)} alt="" className="w-full h-full object-cover" />}</div>
        <div className="px-5 md:px-8 pb-6 -mt-10 md:-mt-12 relative">
          <div className="flex flex-col md:flex-row md:items-end gap-4 justify-between">
            <div className="flex items-end gap-4 min-w-0">
              <span className="ring-4 ring-white rounded-full shrink-0"><Avatar p={p} size="w-20 h-20 md:w-24 md:h-24 text-3xl" /></span>
              <div className="pb-1 min-w-0 pt-12 md:pt-14">
                <h1 className="font-serif text-3xl md:text-4xl truncate" data-testid="profile-name">{p.display_name}</h1>
                <p className="text-xs text-[var(--muted)] flex items-center gap-2 flex-wrap">@{p.handle} <KindBadge kind={p.kind} />{p.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{p.city}{p.state && `, ${p.state}`}</span>}{p.price_range && <span>{p.price_range}</span>}</p>
              </div>
            </div>
            {p.relationship.is_me
              ? <button onClick={() => setEditing(true)} className="btn-outline-gold !py-2.5" data-testid="profile-edit-btn"><Pencil className="w-4 h-4" /> Edit profile</button>
              : <RelationButtons p={p} reload={load} />}
          </div>
          {p.headline && <p className="font-serif text-xl mt-4">{p.headline}</p>}
          {p.bio && <p className="text-sm text-[var(--muted)] mt-2 max-w-2xl whitespace-pre-wrap">{p.bio}</p>}
          <div className="flex flex-wrap gap-6 mt-5 text-sm" data-testid="profile-stats">
            {[["posts", "Posts"], ["followers", "Followers"], ["following", "Following"], ["connections", "Connections"]].map(([k, l]) => <p key={k}><span className="font-serif text-xl mr-1" data-testid={`profile-stat-${k}`}>{p.stats[k]}</span><span className="text-[var(--muted)]">{l}</span></p>)}
            {p.website && <a href={p.website.startsWith("http") ? p.website : `https://${p.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[var(--gold)]"><Globe className="w-4 h-4" /> Website</a>}
            {p.instagram && <a href={`https://instagram.com/${p.instagram.replace("@", "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[var(--gold)]"><Instagram className="w-4 h-4" /> {p.instagram}</a>}
            {p.vendor_id && <Link to={`/v/${p.vendor_id}`} className="text-[var(--gold)]">Vendor page →</Link>}
            {p.wedding?.married && <span className="flex items-center gap-1 text-[var(--gold)]" data-testid="profile-married"><Heart className="w-4 h-4" /> Married {new Date(`${p.wedding.wedding_month}-15`).toLocaleDateString(undefined, { month: "long", year: "numeric" })}</span>}
            {p.wall && <span className="flex items-center gap-1 text-[var(--muted)]" data-testid="profile-wall-visibility">{p.wall.visibility === "public" ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}{p.wall.visibility === "public" ? "Public wall" : "Guests & approved only"}</span>}
          </div>
        </div>
      </div>
      <div className="max-w-2xl space-y-4 mt-6" data-testid="profile-wall">
        {posts === null && <div className="text-center py-14 bg-white border border-[var(--line)] rounded-3xl" data-testid="profile-wall-locked"><Lock className="w-7 h-7 mx-auto text-[var(--gold)] mb-3" /><p className="font-serif text-2xl">This wall is private</p><p className="text-sm text-[var(--muted)] mt-1 mb-4">Only {p.display_name}'s guests and people they approve can see and post here.</p>{user ? <RelationButtons p={p} reload={load} /> : <Link to={`/login?next=/community/u/${p.handle}`} className="btn-gold">Sign in</Link>}</div>}
        {posts !== null && p.relationship.is_me && <Composer me={p} onPosted={(x) => setPosts([x, ...posts])} />}
        {posts !== null && !p.relationship.is_me && mine && <Composer me={mine} wallOf={p.user_id} placeholder={`Write on ${p.display_name}'s wall…`} onPosted={(x) => setPosts([x, ...posts])} />}
        {posts?.length === 0 && <p className="text-center text-[var(--muted)] py-10">No posts yet.</p>}
        {posts?.map((x) => <PostCard key={x.post_id} post={x} user={user} onDeleted={(id) => setPosts(posts.filter((y) => y.post_id !== id))} />)}
      </div>
      {p.relationship.is_me && <EditProfile p={p} open={editing} onClose={() => setEditing(false)} onSaved={(d) => { setEditing(false); if (d.handle !== p.handle) nav(`/community/u/${d.handle}`); else load(); }} />}
    </Shell>
  );
}

export function DirectoryPage() {
  const [f, setF] = useState({ kind: "", city: "", price: "", q: "" });
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => {
      const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
      api.get(`/community/directory?${qs}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(t);
  }, [f]);
  const chip = (k, v, l) => <button key={v} onClick={() => setF({ ...f, [k]: f[k] === v ? "" : v })} data-testid={`dir-${k}-${v || "any"}`} className={`px-4 py-2 rounded-full text-xs whitespace-nowrap border transition-colors ${f[k] === v ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white border-[var(--line)] hover:border-[var(--gold)]"}`}>{l}</button>;
  return (
    <Shell title="Find Wedding Planners, Venues & Vendors" path="/community/directory">
      <div className="bg-white border border-[var(--line)] rounded-3xl p-4 space-y-3" data-testid="directory-filters">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="flex items-center gap-2 bg-[var(--bg)] rounded-full px-4"><Search className="w-4 h-4 text-[var(--muted)]" /><input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search name, style, service" data-testid="dir-search" className="flex-1 min-w-0 bg-transparent py-3 text-sm outline-none" /></label>
          <label className="flex items-center gap-2 bg-[var(--bg)] rounded-full px-4"><MapPin className="w-4 h-4 text-[var(--muted)]" /><input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="City" data-testid="dir-city" className="flex-1 min-w-0 bg-transparent py-3 text-sm outline-none" /></label>
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar">{[["planner", "Planners"], ["venue", "Venues"], ["vendor", "Vendors"], ["creator", "Creators"]].map(([v, l]) => chip("kind", v, l))}<span className="w-px bg-[var(--line)] mx-1" />{["$", "$$", "$$$", "$$$$"].map((v) => chip("price", v, v))}</div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="directory-results">
        {rows === null && [0, 1, 2].map((i) => <div key={i} className="h-56 rounded-3xl bg-white/70 animate-pulse" />)}
        {rows?.length === 0 && <p className="sm:col-span-3 text-center text-[var(--muted)] py-16" data-testid="directory-empty">No pros match yet. Try another city or category.</p>}
        {rows?.map((p, i) => (
          <motion.div key={p.user_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 8) * 0.04 }}>
            <Link to={`/community/u/${p.handle}`} data-testid={`dir-card-${p.handle}`} className="lift block bg-white border border-[var(--line)] rounded-3xl overflow-hidden">
              <div className="h-28 bg-[var(--ink)]">{p.cover_url && <img src={media(p.cover_url)} alt="" className="w-full h-full object-cover" />}</div>
              <div className="p-4 -mt-8">
                <span className="inline-block ring-4 ring-white rounded-full"><Avatar p={p} size="w-14 h-14" /></span>
                <p className="font-serif text-xl mt-1 truncate">{p.display_name}</p>
                <p className="text-[11px] text-[var(--muted)] flex items-center gap-2 mt-0.5"><KindBadge kind={p.kind} />{p.city}{p.price_range && <span>· {p.price_range}</span>}</p>
                {p.headline && <p className="text-sm text-[var(--muted)] mt-2 line-clamp-2">{p.headline}</p>}
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </Shell>
  );
}
